/**
 * Zeiten und Spesen — der Monat auf einen Blick, die Tage auf Wunsch.
 *
 * ═══ WARUM NICHT MEHR ALLE TAGE UNTEREINANDER ═══
 *
 * Die erste Fassung zeigte jeden Tag jedes Mitarbeiters. Bei zwei Testfahrern
 * sah das aufgeräumt aus; bei zehn Fahrern und einundzwanzig Arbeitstagen sind
 * es zweihundert Zeilen, durch die man scrollt, um eine Summe abzuschreiben.
 * Genau das war der Einwand — und er trifft zu: Die Frage am Monatsende lautet
 * „was bekommt Martin", nicht „was war am 14.".
 *
 * Jetzt steht oben eine Zeile je Mitarbeiter. Die Tage kommen erst, wenn man
 * eine Zeile anklickt — und dann nur die eines Mannes. Die Gruppierung
 * zusammenhängender Übernachtungstage bleibt dort erhalten, weil sie genau
 * dort gebraucht wird: beim Nachvollziehen einer einzelnen Reise.
 *
 * ═══ WARUM ES ZWEI AUSGABEN GIBT UND NICHT EINE ═══
 *
 * Sie beantworten verschiedene Fragen. Die Übersicht ist das, was ins
 * Lohnprogramm wandert: ein Betrag je Mitarbeiter. Die Tagesliste ist das,
 * was man vorlegt, wenn jemand nachfragt — mit Datum, Zeiten, Fall und Satz.
 * Eine Datei für beides wäre für die eine Aufgabe zu fein und für die andere
 * zu grob.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Seitenkopf } from '../Rahmen';
import {
  Feld,
  Fehlerkasten,
  Karte,
  Knopf,
  Ladehinweis,
  Marker,
  Meldung,
} from '../gestaltung/bausteine';
import { Filtertabelle, type Spalte } from '../gestaltung/Filtertabelle';
import { useAbfrage } from '../daten/abfrage';
import { alsCsv, dateiname, herunterladen } from '../daten/ausgabe';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { kennzeichenFuer, sehrKurz } from '../daten/spesenmonat';
import {
  arbeitstagKorrigieren,
  arbeitstagNachtragen,
  dauer,
  euro,
  FALLNAME,
  monatLaden,
  MONATSNAMEN,
  reiseZeitraum,
  tagText,
  uhrzeit,
  zeitaenderungenLaden,
  type Mitarbeitermonat,
  type Monatseintrag,
  type Spesentag,
  type Zeitaenderung,
} from '../daten/spesen';

export function SpesenSeite() {
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [monatNr, setMonatNr] = useState(heute.getMonth() + 1);
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null);

  const monat = useAbfrage(() => monatLaden(jahr, monatNr), [jahr, monatNr]);
  const bloecke = useMemo(() => monat.daten ?? [], [monat.daten]);

  function blaettern(richtung: -1 | 1) {
    let m = monatNr + richtung;
    let j = jahr;
    if (m < 1) { m = 12; j -= 1; }
    if (m > 12) { m = 1; j += 1; }
    setMonatNr(m);
    setJahr(j);
    setAufgeklappt(null);
  }

  const monatsname = `${MONATSNAMEN[monatNr - 1]} ${jahr}`;
  const gesamt = bloecke.reduce((n, b) => n + b.summe, 0);
  const ohneZeit = bloecke.reduce((n, b) => n + b.ohneZeit, 0);
  // Werktage, zu denen es gar keinen Datensatz gibt. Das ist die stillere der
  // beiden Lücken: Ein Tag ohne Zeit steht wenigstens in der Liste.
  const ohneEintrag = bloecke.reduce((n, b) => n + b.luecken, 0);

  /** Ein Betrag je Mitarbeiter — das, was ins Lohnprogramm wandert. */
  function uebersichtAusgeben() {
    herunterladen(
      dateiname('Spesen-Uebersicht', monatsname, new Date()),
      alsCsv(bloecke, [
        { kopf: 'Mitarbeiter', wert: (b) => b.name },
        { kopf: 'Monat', wert: () => monatsname },
        { kopf: 'Arbeitstage', wert: (b) => b.tage.length },
        { kopf: 'Naechte', wert: (b) => b.naechte },
        { kopf: 'Verpflegung EUR', wert: (b) => b.verpflegung, zahl: true },
        { kopf: 'Uebernachtung EUR', wert: (b) => b.uebernachtungsgeld, zahl: true },
        { kopf: 'Summe EUR', wert: (b) => b.summe, zahl: true },
        { kopf: 'Tage ohne Zeit', wert: (b) => b.ohneZeit },
      ]),
    );
  }

  /** Jeder Tag einzeln — das, was man vorlegt, wenn jemand nachfragt. */
  function tageAusgeben() {
    const zeilen = bloecke.flatMap((block) =>
      block.tage.map((tag) => ({ block, tag })),
    );

    herunterladen(
      dateiname('Spesen-Tage', monatsname, new Date()),
      alsCsv(zeilen, [
        { kopf: 'Mitarbeiter', wert: (z) => z.block.name },
        { kopf: 'Datum', wert: (z) => z.tag.datum },
        { kopf: 'Beginn', wert: (z) => (z.tag.beginn ? uhrzeit(z.tag.beginn) : '') },
        { kopf: 'Ende', wert: (z) => (z.tag.ende ? uhrzeit(z.tag.ende) : '') },
        { kopf: 'Abwesend Minuten', wert: (z) => z.tag.dauer_minuten },
        { kopf: 'Fall', wert: (z) => FALLNAME[z.tag.spesenfall] },
        { kopf: 'Uebernachtet', wert: (z) => (z.tag.uebernachtung ? 'ja' : 'nein') },
        { kopf: 'Verpflegung EUR', wert: (z) => z.tag.verpflegung, zahl: true },
        { kopf: 'Uebernachtung EUR', wert: (z) => z.tag.uebernachtungsgeld, zahl: true },
        { kopf: 'Summe EUR', wert: (z) => z.tag.summe, zahl: true },
        // Ohne diese Spalte müsste man beim Prüfen raten, warum ein Tag 0 €
        // bringt — Lücke oder gesetzliche Schwelle.
        { kopf: 'Zeit fehlt', wert: (z) => (z.tag.zeit_fehlt ? 'ja' : '') },
      ]),
    );
  }

  const spalten: Spalte<Mitarbeitermonat>[] = [
    {
      schluessel: 'name',
      text: 'Mitarbeiter',
      auswahl: true,
      wert: (b) => b.name,
      zeige: (b) => <strong>{b.name}</strong>,
    },
    {
      schluessel: 'tage',
      text: 'Arbeitstage',
      rechts: true,
      zahl: true,
      wert: (b) => String(b.tage.length),
    },
    {
      schluessel: 'naechte',
      text: 'Nächte',
      rechts: true,
      zahl: true,
      wert: (b) => String(b.naechte),
      zeige: (b) =>
        b.naechte > 0 ? b.naechte : <span className="stumm">—</span>,
    },
    {
      schluessel: 'verpflegung',
      text: 'Verpflegung',
      rechts: true,
      zahl: true,
      wert: (b) => String(b.verpflegung),
      zeige: (b) => euro(b.verpflegung),
    },
    {
      schluessel: 'summe',
      text: 'Summe',
      rechts: true,
      zahl: true,
      wert: (b) => String(b.summe),
      zeige: (b) => <strong>{euro(b.summe)}</strong>,
    },
    {
      // Der wichtigste Filter des Bildschirms: „zeig mir nur die, bei denen
      // etwas fehlt."
      //
      // ZWEI Arten von Lücke, und die zweite ist die stillere: Ein Tag ohne
      // Zeit steht wenigstens in der Liste und bringt sichtbar 0 €. Ein Tag,
      // zu dem es gar keinen Datensatz gibt, stand vorher nirgends — man müsste
      // beim Lesen die Kalendertage mitzählen, um ihn zu bemerken.
      schluessel: 'luecken',
      text: 'Vollständig?',
      auswahl: true,
      wert: (b) =>
        b.luecken > 0 || b.ohneZeit > 0 ? 'etwas fehlt' : 'vollständig',
      zeige: (b) =>
        b.luecken > 0 || b.ohneZeit > 0 ? (
          <span className="reihe" style={{ gap: 6 }}>
            {b.luecken > 0 && (
              <Marker art="schlecht">
                {b.luecken === 1 ? '1 Tag fehlt' : `${b.luecken} Tage fehlen`}
              </Marker>
            )}
            {b.ohneZeit > 0 && (
              <Marker art="warn">
                {b.ohneZeit === 1 ? '1 Tag ohne Zeit' : `${b.ohneZeit} ohne Zeit`}
              </Marker>
            )}
          </span>
        ) : (
          <span className="stumm">vollständig</span>
        ),
      nichtKuerzen: true,
    },
    {
      schluessel: 'tun',
      text: '',
      rechts: true,
      nichtKuerzen: true,
      wert: () => '',
      zeige: (b) => (
        <Knopf
          art="still"
          klein
          onClick={() =>
            {
              setOffenerTag(null);
              setAufgeklappt(
                aufgeklappt === b.mitarbeiterId ? null : b.mitarbeiterId,
              );
            }
          }
        >
          {aufgeklappt === b.mitarbeiterId ? 'Tage zuklappen' : 'Tage ansehen'}
        </Knopf>
      ),
    },
  ];

  const offenerBlock = bloecke.find((b) => b.mitarbeiterId === aufgeklappt) ?? null;

  // Nur für den aufgeklappten Mitarbeiter — das Protokoll aller Tage aller
  // Leute zu laden wäre eine Abfrage, deren Ergebnis fast immer ungesehen bleibt.
  const aenderungen = useAbfrage(
    () => zeitaenderungenLaden((offenerBlock?.tage ?? []).map((t) => t.arbeitstag_id)),
    [offenerBlock?.mitarbeiterId, offenerBlock?.tage.length],
  );

  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [korrekturfehler, setKorrekturfehler] = useState<Klarfehler | null>(null);
  /** Der Fehler der Zeitkorrektur — er steht IN der Maske, nicht oben auf der Seite. */
  const [maskenfehler, setMaskenfehler] = useState<Klarfehler | null>(null);
  /**
   * Welcher Tag des aufgeklappten Mitarbeiters seine Korrekturmaske offen hat.
   *
   * Der Zustand liegt hier und nicht in der Tagesliste, weil ihn zwei Dinge
   * setzen: der Klick auf „Korrigieren" und das Nachtragen. Ein nachgetragener
   * Tag hat noch keine Zeiten — bliebe er zugeklappt, wäre die Aufgabe nur
   * verschoben.
   */
  const [offenerTag, setOffenerTag] = useState<string | null>(null);

  async function nachtragen(mitarbeiterId: string, datum: string) {
    setKorrekturfehler(null);
    setBeschaeftigt(`nachtragen:${datum}`);
    try {
      await arbeitstagNachtragen(mitarbeiterId, datum);
      setOffenerTag(datum);
      monat.auffrischen();
      aenderungen.auffrischen();
    } catch (problem) {
      setKorrekturfehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  /**
   * ═══ DIE MASKE SCHLIESST ERST, WENN GESPEICHERT IST ═══
   *
   * Vorher wurde `korrigieren()` nicht abgewartet, und die Maske schloss sofort.
   * Scheiterte die Standprüfung (der Fahrer hatte gleichzeitig geändert), waren
   * die eingetippten Zeiten weg, und der Tag zeigte die alten Werte — am
   * Monatsletzten leicht zu übersehen, und dann fehlte der Tag in den Spesen.
   *
   * Jetzt gibt es Erfolg oder Misserfolg zurück. Bei Misserfolg bleibt die
   * Maske mit den getippten Zeiten offen, der Fehler steht in ihr, und es wird
   * NICHT aufgefrischt: Mit frischem Stand im Rücken würde der nächste Klick
   * stillschweigend die Änderung des Fahrers überschreiben. Der Weg zum
   * aktuellen Stand ist ein eigener Knopf (`neuLaden`).
   */
  async function korrigieren(
    tag: Spesentag,
    werte: { beginn: string | null; ende: string | null; uebernachtung: boolean },
  ): Promise<boolean> {
    setMaskenfehler(null);
    setBeschaeftigt(tag.arbeitstag_id);
    try {
      await arbeitstagKorrigieren(tag, werte);
      monat.auffrischen();
      aenderungen.auffrischen();
      return true;
    } catch (problem) {
      setMaskenfehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      return false;
    } finally {
      setBeschaeftigt(null);
    }
  }

  /** Den aktuellen Stand holen und die Maske zumachen — die Eingabe wird verworfen. */
  function neuLaden() {
    setMaskenfehler(null);
    monat.auffrischen();
    aenderungen.auffrischen();
    setOffenerTag(null);
  }

  return (
    <>
      <Seitenkopf
        titel="Zeiten und Spesen"
        erklaerung="Verpflegungsmehraufwand nach § 9 Abs. 4a EStG. Gerechnet wird in der Datenbank mit den Sätzen, die am jeweiligen Tag galten — nicht mit den heutigen."
        werkzeuge={
          <div className="reihe" style={{ gap: 8, alignItems: 'center' }}>
            <Knopf art="still" klein onClick={() => blaettern(-1)}>
              ‹
            </Knopf>
            <strong style={{ minWidth: 130, textAlign: 'center' }}>
              {monatsname}
            </strong>
            <Knopf art="still" klein onClick={() => blaettern(1)}>
              ›
            </Knopf>
          </div>
        }
      />

      {korrekturfehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={korrekturfehler} />
        </div>
      )}

      {monat.laedt && !monat.daten ? (
        <Ladehinweis />
      ) : monat.fehler ? (
        <Fehlerkasten fehler={monat.fehler} nochmal={monat.auffrischen} />
      ) : bloecke.length === 0 ? (
        <Meldung art="neutral" titel="Für diesen Monat liegt nichts vor">
          Es wurde kein Arbeitstag erfasst. Erfasst wird in der App — hier
          entstehen keine Zeiten.
        </Meldung>
      ) : (
        <div className="stapel" style={{ gap: 16 }}>
          <div className="vorausschau">
            <div className="vorausschau__block vorausschau__block--hervor">
              <span className="vorausschau__marke">{monatsname}</span>
              <strong className="vorausschau__summe">{euro(gesamt)}</strong>
              <span className="meta">
                {bloecke.length} Mitarbeiter ·{' '}
                {bloecke.reduce((n, b) => n + b.naechte, 0)} Nächte
              </span>
            </div>
            {ohneZeit > 0 && (
              <div className="vorausschau__block">
                <span className="vorausschau__marke">Fehlende Zeiten</span>
                <strong className="vorausschau__summe">{ohneZeit}</strong>
                <span className="meta">
                  {ohneZeit === 1 ? 'Tag bringt' : 'Tage bringen'} so 0,00 €
                </span>
              </div>
            )}
            {ohneEintrag > 0 && (
              <div className="vorausschau__block">
                <span className="vorausschau__marke">Tage ohne Eintrag</span>
                <strong className="vorausschau__summe">{ohneEintrag}</strong>
                <span className="meta">
                  Werktage ohne Arbeitstag · Feiertage zählen mit
                </span>
              </div>
            )}
          </div>

          <Karte
            titel="Monatsübersicht"
            werkzeuge={
              <div className="reihe" style={{ gap: 8 }}>
                <Knopf art="zweit" klein onClick={uebersichtAusgeben}>
                  Übersicht als CSV
                </Knopf>
                <Knopf art="still" klein onClick={tageAusgeben}>
                  Alle Tage als CSV
                </Knopf>
              </div>
            }
          >
            <Filtertabelle
              spalten={spalten}
              zeilen={bloecke}
              schluessel={(b) => b.mitarbeiterId}
              leer="Für diesen Monat liegt nichts vor."
              zaehlung={(gezeigt, gesamtZahl) =>
                gezeigt === gesamtZahl
                  ? `${gesamtZahl} Mitarbeiter`
                  : `${gezeigt} von ${gesamtZahl} Mitarbeitern`
              }
            />
          </Karte>

          {/* Ohne Verlauf fehlen auch die Kennzeichen „nachgetragen" /
              „geändert" — jeder Tag sähe unberührt aus. Das ist genau die
              Auskunft, die man bei einer Lohnprüfung glaubt; also laut. */}
          {offenerBlock && aenderungen.fehler && (
            <div style={{ marginBottom: 12 }}>
              <Fehlerkasten fehler={aenderungen.fehler} nochmal={aenderungen.auffrischen} />
            </div>
          )}

          {offenerBlock && (
            <Tagesliste
              key={offenerBlock.mitarbeiterId}
              block={offenerBlock}
              aenderungen={aenderungen.daten ?? new Map()}
              laeuft={beschaeftigt}
              offenerTag={offenerTag}
              setOffenerTag={(datum) => {
                setMaskenfehler(null);
                setOffenerTag(datum);
              }}
              fehler={maskenfehler}
              neuLaden={neuLaden}
              korrigieren={korrigieren}
              nachtragen={(datum) =>
                void nachtragen(offenerBlock.mitarbeiterId, datum)
              }
            />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Die Tage eines Mitarbeiters — mit den Reisen als Block.
 *
 * Hier bleibt die Gruppierung, und hier gehört sie auch hin: 14 € · 28 € · 14 €
 * an drei aufeinanderfolgenden Tagen ergibt nur zusammen einen Sinn. In der
 * Monatsübersicht wäre sie Ballast, beim Nachvollziehen einer einzelnen Reise
 * ist sie die ganze Erklärung.
 */
function Tagesliste(eigenschaften: {
  block: Mitarbeitermonat;
  aenderungen: Map<string, Zeitaenderung[]>;
  korrigieren: (
    tag: Spesentag,
    werte: { beginn: string | null; ende: string | null; uebernachtung: boolean },
  ) => Promise<boolean>;
  /** Der Fehler der letzten Korrektur — steht in der geöffneten Maske. */
  fehler: Klarfehler | null;
  neuLaden: () => void;
  nachtragen: (datum: string) => void;
  /**
   * Welcher Tag aufgeklappt ist — und warum das die Seite entscheidet und
   * nicht diese Liste.
   *
   * Nach dem Nachtragen soll die Korrekturmaske von selbst aufgehen: Der neue
   * Tag hat noch keine Zeiten, und ihn zugeklappt liegen zu lassen hieße, die
   * Aufgabe nur zu verschieben. Läge der Zustand hier, gäbe es zwei Stellen,
   * die ihn setzen — und beim Zuklappen von Hand spränge er wieder auf.
   */
  offenerTag: string | null;
  setOffenerTag: (datum: string | null) => void;
  laeuft: string | null;
}) {
  const { block, offenerTag, setOffenerTag } = eigenschaften;

  function tagBlock(tag: Spesentag, imVerbund: boolean) {
    const geaendert = eigenschaften.aenderungen.get(tag.arbeitstag_id) ?? [];
    return (
      <div key={tag.datum}>
        <div className={imVerbund ? 'spesentag' : 'spesentag spesentag--einzeln'}>
          <Tagzeile
            tag={tag}
            kennzeichen={kennzeichenFuer(geaendert, tag.mitarbeiter_id)}
          />
          <Knopf
            art="still"
            klein
            onClick={() =>
              setOffenerTag(offenerTag === tag.datum ? null : tag.datum)
            }
          >
            {offenerTag === tag.datum ? 'Zuklappen' : 'Korrigieren'}
          </Knopf>
        </div>

        {offenerTag === tag.datum && (
          <Korrekturmaske
            tag={tag}
            laeuft={eigenschaften.laeuft === tag.arbeitstag_id}
            abbrechen={() => setOffenerTag(null)}
            fehler={eigenschaften.fehler}
            neuLaden={eigenschaften.neuLaden}
            speichern={async (werte) => {
              const gespeichert = await eigenschaften.korrigieren(tag, werte);
              // Zu ist sie erst, wenn es geklappt hat.
              if (gespeichert) setOffenerTag(null);
              return gespeichert;
            }}
          />
        )}

        {/* Was hier geändert wurde, steht darunter — am GEÖFFNETEN Tag
            (HJ-030). Nicht in einem Nebenbildschirm: Wer eine Zeit
            korrigiert, muss sehen, ob sie noch die ursprüngliche ist. Dass
            etwas geändert wurde, zeigt schon das Kennzeichen in der Zeile. */}
        {offenerTag === tag.datum && geaendert.length > 0 && (
          <div className="zeitprotokoll">
            {geaendert.map((a) => (
              <span key={a.id}>
                {a.feld}: {a.alt ?? '—'} → {a.neu ?? '—'} ·{' '}
                {a.durch_name ?? 'unbekannt'} am{' '}
                {new Date(a.erstellt_am).toLocaleDateString('de-DE')}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Karte
      titel={`${block.name} — jeder Tag`}
      werkzeuge={
        <span className="meta">
          {euro(block.verpflegung)} Verpflegung
          {block.uebernachtungsgeld > 0 &&
            ` · ${euro(block.uebernachtungsgeld)} Übernachtung`}
        </span>
      }
    >
      {/* Enger Abstand, weil zwischen zwei Lücken kein Platz nötig ist. Die
          erfassten Tage tragen ihren eigenen Rahmen und stehen dadurch
          trotzdem für sich. */}
      <div className="stapel" style={{ gap: 5 }}>
        {block.eintraege.map((eintrag: Monatseintrag) => {
          if (eintrag.art === 'reise') {
            return (
              <Reiserahmen key={eintrag.datum} gruppe={eintrag.gruppe}>
                {eintrag.gruppe.map((t) => tagBlock(t, true))}
              </Reiserahmen>
            );
          }
          if (eintrag.art === 'tag') return tagBlock(eintrag.tag, false);
          return (
            <Fehltag
              key={eintrag.datum}
              datum={eintrag.datum}
              werktag={eintrag.werktag}
              laeuft={eigenschaften.laeuft === `nachtragen:${eintrag.datum}`}
              nachtragen={() => eigenschaften.nachtragen(eintrag.datum)}
            />
          );
        })}
      </div>
    </Karte>
  );
}

/**
 * Ein Kalendertag, zu dem es keinen Arbeitstag gibt.
 *
 * ═══ WARUM DIE LÜCKE EINE ZEILE BEKOMMT ═══
 *
 * Weil sie sonst keine ist. Vorher standen nur die erfassten Tage untereinander
 * — zwischen dem 12. und dem 14. fehlte einfach eine Zeile, und niemand zählt
 * beim Lesen die Kalendertage mit. So verschwindet Verpflegungsmehraufwand:
 * still, und dem Fahrer fällt es frühestens auf der Lohnabrechnung auf.
 *
 * ═══ WARUM DAS WOCHENENDE LEISER IST ═══
 *
 * Ein Sonntag ohne Eintrag ist der Normalfall. Stünde er so laut da wie ein
 * fehlender Dienstag, wären zwei Drittel der Warnungen keine — und dann liest
 * man die restlichen auch nicht mehr.
 */
function Fehltag(eigenschaften: {
  datum: string;
  werktag: boolean;
  laeuft: boolean;
  nachtragen: () => void;
}) {
  return (
    <div
      className={`fehltag${eigenschaften.werktag ? '' : ' fehltag--ruhetag'}`}
    >
      <span className="fehltag__tag">{tagText(eigenschaften.datum)}</span>
      <span className="fehltag__hinweis">
        {eigenschaften.werktag ? 'Kein Eintrag' : 'Wochenende, kein Eintrag'}
      </span>
      <Knopf
        art="still"
        klein
        disabled={eigenschaften.laeuft}
        onClick={eigenschaften.nachtragen}
      >
        Nachtragen
      </Knopf>
    </div>
  );
}

function Reiserahmen(eigenschaften: {
  gruppe: Spesentag[];
  children: ReactNode;
}) {
  const { gruppe } = eigenschaften;
  const summe = gruppe.reduce((n, t) => n + (t.summe ?? 0), 0);
  const naechte = gruppe.filter((t) => t.uebernachtung).length;
  const beginntFrueher =
    gruppe[0].spesenfall === 'zwischentag' || gruppe[0].spesenfall === 'abreisetag';
  const laeuftWeiter = gruppe[gruppe.length - 1].uebernachtung;

  return (
    <div className="reise">
      <div className="reise__kopf">
        <div>
          <span className="reise__marke">
            {gruppe.length === 1
              ? 'Unterwegs geblieben'
              : `${gruppe.length} Tage unterwegs`}
          </span>
          <strong className="reise__zeitraum">{reiseZeitraum(gruppe)}</strong>
        </div>
        <div style={{ textAlign: 'right' }}>
          <strong className="reise__summe">{euro(summe)}</strong>
          <span className="meta" style={{ display: 'block' }}>
            {naechte} {naechte === 1 ? 'Nacht' : 'Nächte'}
          </span>
        </div>
      </div>

      {/* Am Monatsersten fehlt der Anfang der Reise. Ohne diesen Satz sähe ein
          erster Tag mit 28 € aus wie ein Fehler. */}
      {beginntFrueher && (
        <p className="reise__rand">Angefangen hat die Tour im Vormonat.</p>
      )}

      <div>{eigenschaften.children}</div>

      {laeuftWeiter && (
        <p className="reise__rand">
          Die letzte Nacht ist noch unterwegs — die Heimfahrt liegt im
          Folgemonat.
        </p>
      )}
    </div>
  );
}

/**
 * Einen Arbeitstag korrigieren.
 *
 * ═══ WARUM DAS HIER UND NICHT NUR IN DER APP GEHT ═══
 *
 * Weil es sonst nach dem Monatswechsel nirgends mehr ginge. Der Fahrer
 * korrigiert selbst, solange sein Monat läuft; danach sperrt ihn die Datenbank
 * aus. Eine im Oktober bemerkte Lücke aus dem September wäre ohne diese Maske
 * für immer eine Lücke — und der Fahrer bekäme sein Geld nicht.
 *
 * Jede Änderung wird mitgeschrieben. Sie steht danach unter dem Tag, mit Namen
 * und Zeitpunkt: Eine Lohngrundlage, die jemand nachträglich anfasst, ohne dass
 * es irgendwo steht, ist bei einer Prüfung nur eine Behauptung.
 */
function Korrekturmaske(eigenschaften: {
  tag: Spesentag;
  laeuft: boolean;
  abbrechen: () => void;
  fehler: Klarfehler | null;
  neuLaden: () => void;
  speichern: (werte: {
    beginn: string | null;
    ende: string | null;
    uebernachtung: boolean;
  }) => Promise<boolean>;
}) {
  const { tag } = eigenschaften;

  /** „2026-08-27T04:30:00Z" → „04:30" in Ortszeit, wie es dasteht. */
  function alsFeld(wert: string | null): string {
    if (!wert) return '';
    const d = new Date(wert);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  const [beginn, setBeginn] = useState(alsFeld(tag.beginn));
  const [ende, setEnde] = useState(alsFeld(tag.ende));
  const [nacht, setNacht] = useState(tag.uebernachtung);

  /** „04:30" → Minuten seit Mitternacht, oder null. */
  function minuten(feld: string): number | null {
    if (!feld) return null;
    const [stunde, minute] = feld.split(':').map(Number);
    return stunde * 60 + minute;
  }

  /**
   * „04:30" → der Zeitpunkt, den die Datenbank erwartet.
   *
   * ═══ WARUM DAS FELD NICHT REICHT, UM DEN TAG ZU KENNEN ═══
   *
   * Ein Uhrzeitfeld sagt „00:45" und verschweigt, welcher Kalendertag gemeint
   * ist. Vorher wurde deshalb immer auf `tag.datum` geschrieben — und ein
   * Feierabend nach Mitternacht landete damit VIER STUNDEN VOR dem Beginn
   * desselben Tages. Die Folge sah nach einem Datenfehler aus und war ein
   * Rechenfehler: `dauer_minuten` wurde negativ, die Verpflegungspauschale fiel
   * auf 0,00 €, und im Feld stand weiterhin unauffällig „00:45".
   *
   * Ausgelöst hat es nicht einmal eine Zeitänderung — es genügte, „Übernachtet"
   * umzustellen und zu speichern.
   *
   * Die Regel ist die des Fahrtags, nicht die des Kalenders: Ein Ende, das VOR
   * dem Beginn liegt, gehört zur Nacht danach.
   */
  function alsZeitpunkt(feld: string, folgetagWennVorBeginn = false): string | null {
    if (!feld) return null;
    const wann = minuten(feld);
    const start = minuten(beginn);
    const d = new Date(tag.datum + 'T00:00:00');
    if (
      folgetagWennVorBeginn &&
      wann !== null &&
      start !== null &&
      wann < start
    ) {
      d.setDate(d.getDate() + 1);
    }
    const [stunde, minute] = feld.split(':').map(Number);
    d.setHours(stunde, minute, 0, 0);
    return d.toISOString();
  }

  return (
    <div className="korrektur">
      <div className="feldreihe">
        <Feld marke="Beginn">
          <input
            type="time"
            value={beginn}
            disabled={eigenschaften.laeuft}
            onChange={(e) => setBeginn(e.target.value)}
          />
        </Feld>
        <Feld marke="Ende">
          <input
            type="time"
            value={ende}
            disabled={eigenschaften.laeuft}
            onChange={(e) => setEnde(e.target.value)}
          />
        </Feld>
        <Feld
          marke="Übernachtet"
          hinweis="Entscheidet auch über den Satz des Folgetags."
        >
          <select
            value={nacht ? 'ja' : 'nein'}
            disabled={eigenschaften.laeuft}
            onChange={(e) => setNacht(e.target.value === 'ja')}
          >
            <option value="nein">nein</option>
            <option value="ja">ja</option>
          </select>
        </Feld>
      </div>

      {/* Der Fehler steht dort, wo gehandelt wird — in der Maske. Oben auf der
          Seite sähe ihn niemand, der gerade Zeiten tippt. */}
      {eigenschaften.fehler && (
        <div style={{ margin: '10px 0' }}>
          <Fehlerkasten fehler={eigenschaften.fehler} />
          <div style={{ marginTop: 8 }}>
            <Knopf art="zweit" klein onClick={eigenschaften.neuLaden}>
              Aktuellen Stand laden
            </Knopf>
            <span className="meta">
              {' '}
              Deine eingetippten Zeiten bleiben bis dahin stehen.
            </span>
          </div>
        </div>
      )}

      <div className="reihe reihe--rechts">
        <Knopf art="still" klein onClick={eigenschaften.abbrechen}>
          Abbrechen
        </Knopf>
        <Knopf
          art="haupt"
          klein
          disabled={eigenschaften.laeuft}
          onClick={() =>
            void eigenschaften.speichern({
              beginn: alsZeitpunkt(beginn),
              // Nur das Ende darf auf den Folgetag rutschen. Ein Beginn vor dem
              // Beginn wäre keine Aussage.
              ende: alsZeitpunkt(ende, true),
              uebernachtung: nacht,
            })
          }
        >
          {eigenschaften.laeuft ? 'Wird gespeichert …' : 'Korrektur speichern'}
        </Knopf>
      </div>
    </div>
  );
}

/**
 * ═══ „UNTERWEGS" STATT „ABWESEND" (HJ-029) ═══
 *
 * Für die Verpflegungspauschale zählt die Abwesenheit von Wohnung und erster
 * Tätigkeitsstätte — Beginn bis Ende. Rechnerisch stimmte „0 h 01 min
 * abwesend" also; gelesen wurde es im Testlauf aber als „war eine Minute
 * nicht da". „Unterwegs" sagt dasselbe so, wie man es im Büro meint. In der
 * CSV für die Lohnabrechnung bleibt die Spalte „Abwesend Minuten" — dort ist
 * der Fachbegriff richtig, und wer die Datei weiterverarbeitet, verlässt sich
 * auf den Spaltennamen.
 *
 * ═══ VERLAUF ZU, KENNZEICHEN SICHTBAR (HJ-030) ═══
 *
 * Der Verlauf stand bei jedem Tag offen und machte die Liste unruhig. Er
 * steht jetzt erst am geöffneten Tag; in der Zeile bleibt ein Kennzeichen
 * „nachgetragen" oder „geändert" — ein nachträglich angefasster Tag ist
 * Lohngrundlage und muss das auf den ersten Blick zeigen.
 */
function Tagzeile(eigenschaften: {
  tag: Spesentag;
  kennzeichen: 'nachgetragen' | 'geändert' | null;
}) {
  const { tag, kennzeichen } = eigenschaften;

  return (
    <>
      <div className="spesentag__datum">
        <strong>{tagText(tag.datum)}</strong>
        {tag.spesenfall !== 'eintaegig' && (
          <span className="meta">{FALLNAME[tag.spesenfall]}</span>
        )}
        {kennzeichen && <Marker art="neutral">{kennzeichen}</Marker>}
      </div>

      <div className="spesentag__zeit">
        {tag.zeit_fehlt ? (
          <Marker art="warn">Zeit fehlt</Marker>
        ) : (
          <>
            {uhrzeit(tag.beginn)} – {uhrzeit(tag.ende)}
            <span className="meta"> · {dauer(tag.dauer_minuten)} unterwegs</span>
            {sehrKurz(tag.dauer_minuten) && (
              <>
                {' '}
                <Marker art="warn">sehr kurz – Test?</Marker>
              </>
            )}
          </>
        )}
      </div>

      <div className="spesentag__betraege">
        <span>{euro(tag.verpflegung)}</span>
        {/* Die Übernachtungsspalte erscheint nur, wenn es dafür auch Geld gibt.
            Haid & Jerg zahlt die Kraftfahrerpauschale nicht — eine Spalte
            voller 0,00 € wäre eine Frage, die jedes Mal neu gestellt wird. */}
        {(tag.uebernachtungsgeld ?? 0) > 0 && (
          <span>🛏 {euro(tag.uebernachtungsgeld)}</span>
        )}
        <strong>{euro(tag.summe)}</strong>
      </div>
    </>
  );
}
